import React, { useEffect, useState } from "react";
import "../styles/market.css";
import { Card, CardBody, CardHeader } from "reactstrap";
import { useSelector } from "react-redux";
import { fetchTduHistory } from "../api/market";
import moment from "moment";
import MyTooltip from "./Common/Tooltip";
import { selectUtility } from "../store/utility/selector";
import DataTable from "react-data-table-component";
import { Typography } from "@mui/material";
import { BarDatum, ResponsiveBar } from "@nivo/bar";
import {
  ChartBlock,
  ChartTitle,
  ChartWrapper,
  CustomTooltip,
  CustomTooltipX,
  CustomTooltipY,
  CustomTooltipYLabel,
  CustomTooltipYVal,
} from "./Styles/Chart.styled";
import { BaseChartProps } from "../common/chart-config";
import { NoResult } from "./Common/NoResult";
import { mergeObjects } from "./Charts/helper";
import Loader from "./Common/Loader";

const TDU_DeliveryCharges = () => {
  const currentLocation = useSelector<
    any,
    { state_id: string; state_name: string }
  >((state) => state.Location.currentLocation);

  const [isLoading, setIsLoading] = useState(false);
  const [tduData, setTduData] = useState<any>({});
  const currentUtility = useSelector(selectUtility);

  const getTduHistory = async () => {
    if (!currentUtility?.utility_id) return;
    
    setIsLoading(true);

    const months: string[] = [];
    for (let i = 0; i < 12; i++) {
      const month = moment().subtract(i, "months").format("YYYY-MM");
      months.unshift(month);
    }

    try {
      const res = await fetchTduHistory(currentUtility.utility_id, months);
      if (res.status === 1) {
        setTduData(res.response || {});
      } else {
        setTduData({});
      }
    } catch (error) {
      console.error("Failed to fetch TDU history:", error);
      setTduData({});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUtility) return;
    getTduHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUtility]);

  const prepareBaseChartData = (): BarDatum[] => {
    const chartData: BarDatum[] = [];
    const sortedMonths = Object.keys(tduData).sort();

    sortedMonths.forEach((month) => {
      const data = tduData[month];
      if (data) {
        const baseCharge = parseFloat(data.tdu_base || 0);
        chartData.push({
          month: moment(month, "YYYY-MM").format("MMM YYYY"),
          "TDU Base Charge": baseCharge,
        });
      }
    });

    return chartData;
  };

  const prepareEnergyChartData = (): BarDatum[] => {
    const chartData: BarDatum[] = [];
    const sortedMonths = Object.keys(tduData).sort();

    sortedMonths.forEach((month) => {
      const data = tduData[month];
      if (data) {
        const tduPer = parseFloat(data.tdu_per || 0);
        const energyCharge = tduPer < 1 ? tduPer * 100 : tduPer;
        chartData.push({
          month: moment(month, "YYYY-MM").format("MMM YYYY"),
          "TDU Energy Charge": energyCharge,
        });
      }
    });

    return chartData;
  };

  const baseChartData = prepareBaseChartData();
  const energyChartData = prepareEnergyChartData();

  const prepareTableData = (type: "base" | "energy") => {
    const sortedMonths = Object.keys(tduData).sort();
    const row: any = {
      id: type === "base" ? "TDU Base Charge" : "TDU Energy Charge",
    };

    sortedMonths.forEach((month) => {
      const data = tduData[month];
      if (data) {
        const baseCharge = parseFloat(data.tdu_base || 0);
        const tduPer = parseFloat(data.tdu_per || 0);
        const energyCharge = tduPer < 1 ? tduPer * 100 : tduPer;
        const formattedMonth = moment(month, "YYYY-MM").format("MMM YYYY");

        if (type === "base") {
          row[formattedMonth] = baseCharge.toFixed(2);
        } else {
          row[formattedMonth] = energyCharge.toFixed(4);
        }
      }
    });

    return [row];
  };

  const baseTableData = prepareTableData("base");
  const energyTableData = prepareTableData("energy");

  const isMobile = document.body.clientWidth <= 786;

  const baseChartConfig = {
    keys: ["TDU Base Charge"],
    indexBy: "month",
    margin: { bottom: 100 },
    padding: 0.7,
    groupMode: "stacked" as const,
    valueScale: { type: "linear" },
    indexScale: { type: "band", round: true },
    colors: ["rgb(55, 126, 184)"],
    axisBottom: {
      tickValues: isMobile ? "every 3 months" : "every month",
      format: (value: string) => value,
    },
    axisLeft: {
      legend: "Rate (in dollars)",
    },
    enableLabel: false,
    fill: [],
    legends: [
      {
        anchor: "bottom",
        direction: "row",
        justify: false,
        translateY: 80,
        itemsSpacing: 8,
        itemDirection: "top-to-bottom",
        symbolShape: "circle",
        itemWidth: 180,
        itemHeight: 20,
        symbolSize: 14,
      },
    ],
    tooltip: (point: any) => {
      if (point && point.data) {
        const { data } = point;
        if (data && data.month) {
          const baseCharge = data["TDU Base Charge"];
          return (
            <CustomTooltip>
              <CustomTooltipX>{data.month}</CustomTooltipX>
              {baseCharge !== null && baseCharge !== undefined && (
                <CustomTooltipY>
                  <CustomTooltipYLabel color="rgb(55, 126, 184)">
                    <i />
                    <span>TDU Base Charge</span>
                  </CustomTooltipYLabel>
                  <CustomTooltipYVal>{`$${baseCharge.toFixed(2)}`}</CustomTooltipYVal>
                </CustomTooltipY>
              )}
            </CustomTooltip>
          );
        }
      }
      return null;
    },
  };

  const energyChartConfig = {
    ...baseChartConfig,
    keys: ["TDU Energy Charge"],
    colors: ["rgb(220, 38, 38)"],
    axisLeft: {
      legend: "Rate/kWh (in cents)",
    },
    tooltip: (point: any) => {
      if (point && point.data) {
        const { data } = point;
        if (data && data.month) {
          const energyCharge = data["TDU Energy Charge"];
          return (
            <CustomTooltip>
              <CustomTooltipX>{data.month}</CustomTooltipX>
              {energyCharge !== null && energyCharge !== undefined && (
                <CustomTooltipY>
                  <CustomTooltipYLabel color="rgb(220, 38, 38)">
                    <i />
                    <span>TDU Energy Charge</span>
                  </CustomTooltipYLabel>
                  <CustomTooltipYVal>{`${energyCharge.toFixed(4)}¢`}</CustomTooltipYVal>
                </CustomTooltipY>
              )}
            </CustomTooltip>
          );
        }
      }
      return null;
    },
  };

  const baseConfig = mergeObjects(BaseChartProps, baseChartConfig);
  const energyConfig = mergeObjects(BaseChartProps, energyChartConfig);

  return (
    <Card style={{ minHeight: "600px" }}>
      <CardHeader className="align-items-center justify-content-between d-flex">
        <div>
          <h4 className="card-title mb-0 flex-grow-1">TDU Trend</h4>
          {currentLocation && currentUtility && (
            <Typography variant="subtitle2" className="text-muted">
              {currentUtility.utility_name}
            </Typography>
          )}
        </div>
        <MyTooltip>
          <div>
            <h6 style={{ color: "#fff" }}>Data Source</h6>
            <p>
              <a
                href="https://www.txu.com/-/media/Project/VistraApps/DT/Files/Content-Pages/Help-Center/20251207_-_TDU_Charges_Web_Update_RES_WEB_DEC07_2025_ENG_DETAIL.pdf"
                target="_blank"
                rel="noreferrer"
              >
                https://www.txu.com/-/media/Project/VistraApps/DT/Files/Content-Pages/Help-Center/20251207_-_TDU_Charges_Web_Update_RES_WEB_DEC07_2025_ENG_DETAIL.pdf
              </a>
            </p>
          </div>
        </MyTooltip>
      </CardHeader>
      <CardBody>
        <ChartBlock>
          <ChartTitle>Total TDU Charges Per kWh</ChartTitle>
          <ChartWrapper height="400px">
            {isLoading ? (
              <Loader />
            ) : energyChartData.length === 0 ? (
              <NoResult />
            ) : (
              <ResponsiveBar {...energyConfig} data={energyChartData} />
            )}
          </ChartWrapper>
        </ChartBlock>

        {!isLoading && energyTableData.length > 0 && (
          <TrendTable data={energyTableData} />
        )}

        <ChartBlock>
          <ChartTitle>Total TDU Charges Per Month</ChartTitle>
          <ChartWrapper height="400px">
            {isLoading ? (
              <Loader />
            ) : baseChartData.length === 0 ? (
              <NoResult />
            ) : (
              <ResponsiveBar {...baseConfig} data={baseChartData} />
            )}
          </ChartWrapper>
        </ChartBlock>

        {!isLoading && baseTableData.length > 0 && (
          <TrendTable data={baseTableData} />
        )}
      </CardBody>
    </Card>
  );
};

const TrendTable = ({ data }: { data: any }) => {
  const frozenColumns = [
    {
      name: <span className="font-weight-bold fs-14">Rate Type</span>,
      selector: (row: any) => row.id,
    },
  ];

  const scrollableColumns = Object.keys(data[0] || {})
    .filter((key) => key !== "id")
    .map((dateKey) => ({
      name: dateKey,
      selector: (row: any) => {
        const value = Number(row[dateKey]);
        if (row.id === "TDU Base Charge") {
          return `$${value.toFixed(2)}`;
        } else {
          return `${value.toFixed(4)}¢`;
        }
      },
    }));

  if (data.length === 0) return null;

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#F2F2F2",
      },
    },
  };

  return (
    <div className="combined-table mb-5">
      <div className="fronzen-table">
        <DataTable
          columns={frozenColumns}
          data={data}
          paginationPerPage={10}
          customStyles={{
            cells: { style: { backgroundColor: "#fafafa" } },
            ...customStyles,
          }}
          pagination={false}
        />
      </div>
      <div className="scrollable-table">
        <DataTable
          columns={scrollableColumns}
          data={data}
          paginationPerPage={10}
          customStyles={customStyles}
          pagination={false}
        />
      </div>
    </div>
  );
};

export default TDU_DeliveryCharges;



